import React, { useState } from "react";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import ConditionFilterForm from "../components/ConditionFilterForm";
import MatchingPreviewGrid from "../components/MatchingPreviewGrid";
import {
  SafetyWorkflowBanner,
  CostEstimatorPanel,
  OperationConfirmModal,
  OperationProgressModal,
  OperationResultModal,
  ExportMatchingToolbar,
} from "../components/OperationWorkflow";
import { dryRunUpdateByCondition, executeUpdateByCondition } from "../services/updateByConditionService";
import { estimateOperationCost } from "../services/costEstimatorService";
import { writeAuditLog } from "../services/auditLogService";

export default function UpdateByConditionPage() {
  const { collectionName, selectCollection, allCollections, user, can, Permission } = useFirestoreAdmin();
  const [filter, setFilter] = useState({ field: "", op: "==", value: "" });
  const [updateRequest, setUpdateRequest] = useState({
    updateType: "setValue",
    targetField: "",
    newValue: "",
    renameTo: "",
    sourceField: "",
  });
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);

  const cost = preview
    ? estimateOperationCost({
        reads: preview.affectedDocuments + 20,
        writes: preview.affectedDocuments * 2,
        deletes: 0,
        storageBytes: preview.affectedDocuments * 512,
      })
    : null;

  const runDryRun = async () => {
    if (!can(Permission.edit)) return toast.error("Edit permission required");
    setLoading(true);
    try {
      const report = await dryRunUpdateByCondition(collectionName, filter, updateRequest);
      setPreview(report);
      toast.success(`${report.affectedDocuments} match(es)`);
    } catch (e) {
      toast.error(e?.message || "Dry run failed");
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    setExecuting(true);
    setConfirmOpen(false);
    try {
      const outcome = await executeUpdateByCondition({
        collectionName,
        filter,
        updateRequest,
        adminUid: user?.uid,
        adminName: user?.email,
        onProgress: setProgress,
      });
      await writeAuditLog({
        adminName: user?.email,
        adminUid: user?.uid,
        action: "update_by_condition",
        collection: collectionName,
        oldValues: { filter, updateRequest },
        newValues: outcome,
      });
      setResult(outcome);
      toast.success(`Updated ${outcome.updated}`);
    } catch (e) {
      toast.error(e?.message || "Update failed");
    } finally {
      setExecuting(false);
      setProgress(null);
    }
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold">Update by Condition</h1>
        <p className="text-sm text-slate-600">Filter documents and apply bulk field updates with backup and audit.</p>
      </header>
      <SafetyWorkflowBanner />
      <div className="fa-card p-4">
        <ConditionFilterForm
          collectionName={collectionName}
          allCollections={allCollections}
          onCollectionChange={selectCollection}
          filter={filter}
          onFilterChange={setFilter}
          updateRequest={updateRequest}
          onUpdateChange={setUpdateRequest}
          showUpdateSection
        />
        <button type="button" className="fa-btn fa-btn-primary mt-4" onClick={runDryRun} disabled={loading}>
          {loading ? "Running dry run…" : "Dry run"}
        </button>
      </div>
      {preview && (
        <>
          <CostEstimatorPanel cost={cost} />
          <div className="fa-card space-y-2 p-4 text-sm">
            <p><strong>Matching:</strong> {preview.affectedDocuments}</p>
            <p><strong>Changed fields:</strong> {preview.changedFields?.join(", ")}</p>
            <p><strong>Est. batches:</strong> {preview.estimatedBatches} · <strong>~{preview.estimatedSeconds}s</strong></p>
            {preview.warnings?.map((w, i) => <p key={i} className="text-amber-700">⚠ {w}</p>)}
          </div>
          <ExportMatchingToolbar rows={preview.previewRows} collectionName={collectionName} />
          <MatchingPreviewGrid rows={preview.previewRows} showNewValue />
          <button type="button" className="fa-btn fa-btn-danger" onClick={() => setConfirmOpen(true)} disabled={!preview.affectedDocuments}>
            Proceed to confirmation
          </button>
        </>
      )}
      <OperationConfirmModal
        open={confirmOpen}
        title="Update by condition — dry run"
        confirmWord="UPDATE"
        preview={preview}
        executing={executing}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={execute}
      >
        <p>Backup → batch update → audit log. Type UPDATE to execute.</p>
      </OperationConfirmModal>
      <OperationProgressModal progress={progress} label="Updating…" />
      <OperationResultModal
        result={result}
        labels={{ success: "Updated", failed: "Failed" }}
        onClose={() => setResult(null)}
      />
    </div>
  );
}
